import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Loader2, AlertCircle, ArrowLeft, Store, Instagram, MoreVertical, Plus, Camera, Mic, CheckCheck, Send, FileText, Image as ImageIcon, Video, Music, User, X, RotateCcw, Circle, Square, RefreshCw, Download, Share2 } from 'lucide-react';
import axios from 'axios';
import BuyerOnboardingPrompt from '../components/shared/BuyerOnboardingPrompt';
import BuyerNotificationAlerts from '../components/shared/BuyerNotificationAlerts';
import { getBuyerId, syncBuyerPush, ensureServiceWorker } from '../lib/push';

/**
 * Buyer-facing WhatsApp-lookalike chat view for a shared collection link.
 *
 * Route: /c/:id  (id may be the collection UUID or its short_code)
 *
 * Sequence rendered (permanent contract per Feb-2026 spec):
 *   1. Date pill + encryption notice (WhatsApp-style)
 *   2. Welcome bubble: "Hi, / Welcome to <SELLER>, / Below is the latest collection"
 *   3. One image bubble per product — in seller's upload order — using the
 *      seller's actual uploaded pixels (no poster overlay, no reformatting).
 *   4. Final bubble: seller's collection description RAW / VERBATIM (never
 *      cleaned, summarised or reformatted; that AI cleanup is only for the
 *      poster generator, never for this buyer message).
 *
 * Nothing else appears between images. Product data comes from
 * GET /api/collections/{id}/view — reuses the existing endpoint.
 */
export default function CollectionView() {
  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sellerDetailsOpen, setSellerDetailsOpen] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  // Interactive-chat state (v2 — Feb 2026).
  // buyer_id is a STABLE per-browser identifier stored in localStorage under
  // `fabtexs.buyer.id`. It is GLOBAL (NOT per-collection) so the SAME buyer
  // resumes the same conversation with the SAME seller across every
  // collection they visit. The backend keys buyer_chats on (seller_id, buyer_id).
  // Resolved by the shared helper: localStorage -> cookie mirror -> minted.
  // The mirror exists because losing this id orphans the buyer from every
  // buyer_connections row and every buyer_notifications record (empty bell, and
  // the server-side fanout stops addressing them) — which is exactly what a PWA
  // reinstall used to cause. It only ever restores the SAME id.
  const [sessionId] = useState(() => getBuyerId());
  const [chatMessages, setChatMessages] = useState([]);   // [{role:'buyer'|'seller', text, at, kind, url, order_card}]
  const [catEvent, setCatEvent] = useState(null);          // catalogue-presentation event (dedicated store)
  const [catExpanded, setCatExpanded] = useState(false);   // buyer re-expanded the compact catalogue
  const [draft, setDraft] = useState('');

  // Product selected via the Catalogue "Order this" action, e.g.
  // /c/:id?product=LDW-1094 — the chat must show ONLY this product (never the
  // whole collection) and make it the conversation context.
  const focusProduct = new URLSearchParams(location.search || '').get('product');
  const focusInjectedRef = useRef(false);
  const [sending, setSending] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  // Feb 2026 — Product-image viewer state. When set, renders a full-screen
  // overlay with Save / Share / Close. Any product image tap opens it. No
  // new endpoint or access change — these image URLs are already publicly
  // fetchable by the buyer's browser (they were rendered as <img src>).
  const [imgView, setImgView] = useState(null); // { src, alt } | null
  const [recording, setRecording] = useState(false);
  const [recStart, setRecStart] = useState(0);
  const mediaRecRef = useRef(null);
  const recChunksRef = useRef([]);
  const recCancelledRef = useRef(false);
  const fileDocRef = useRef(null);
  const filePhotoRef = useRef(null);
  const fileVideoRef = useRef(null);
  const fileAudioRef = useRef(null);
  const fileContactRef = useRef(null);
  const cameraRef = useRef(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [orderModal, setOrderModal] = useState(null);  // { card, msgIndex }
  // ── Realtime (SSE) infra ──────────────────────────────────────────────
  // seenIdsRef: message ids already rendered → idempotent de-dup so a live
  // event / reconnect / history reload never shows a message twice.
  const seenIdsRef = useRef(new Set());
  const histCountRef = useRef(0);              // #messages loaded → SSE start cursor
  const [histLoaded, setHistLoaded] = useState(false);
  // ── Buyer network onboarding (Jun 2026) ──────────────────────────────
  const [connection, setConnection] = useState(null);      // {seller_id, onboarding_decision, ...}
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const onboardDecidedRef = useRef(false);
  const onboardArmedRef = useRef(false);

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL;
    setLoading(true);
    axios
      .get(`${base}/api/collections/${id}/view`)
      .then(({ data }) => setData(data))
      .catch((err) => setError(err.response?.data?.detail || 'Collection not found.'))
      .finally(() => setLoading(false));
  }, [id]);

  // Load prior conversation for this session.
  useEffect(() => {
    if (!id || !sessionId) return;
    const base = process.env.REACT_APP_BACKEND_URL;
    axios
      .get(`${base}/api/collections/${id}/chat/${sessionId}`)
      .then(({ data }) => {
        const msgs = data.messages || [];
        msgs.forEach((m) => { if (m.id) seenIdsRef.current.add(m.id); });
        histCountRef.current = msgs.length;
        setChatMessages(msgs);
      })
      .catch(() => {})
      .finally(() => setHistLoaded(true));
  }, [id, sessionId]);

  // ── Realtime buyer chat (Server-Sent Events) ─────────────────────────
  // Opens AFTER history loads (so we start the cursor past what we already
  // have). Every seller/system push (payment confirmed, shipped, rejected,
  // expired, …) arrives here within ~1.5s with NO refresh. The browser
  // auto-reconnects and resends Last-Event-ID, so missed messages are
  // recovered; de-dup by message id keeps exactly one visible copy.
  useEffect(() => {
    if (!id || !sessionId || !histLoaded) return;
    const base = process.env.REACT_APP_BACKEND_URL;
    const url = `${base}/api/collections/${id}/chat/${sessionId}/stream?after=${histCountRef.current}`;
    let es;
    try { es = new EventSource(url); } catch (_e) { return; }
    es.addEventListener('message', (ev) => {
      let msg;
      try { msg = JSON.parse(ev.data); } catch (_e) { return; }
      if (!msg) return;
      // The buyer authored their own bubbles locally — never echo them back.
      if (msg.role === 'buyer') { if (msg.id) seenIdsRef.current.add(msg.id); return; }
      if (msg.id && seenIdsRef.current.has(msg.id)) return;
      if (msg.id) seenIdsRef.current.add(msg.id);
      setChatMessages((prev) => {
        if (msg.id && prev.some((m) => m.id === msg.id)) return prev;
        // Reconcile an optimistic (id-less) counterpart shown a moment ago.
        const sig = (m) => `${m.role}|${m.kind || ''}|${m.order_number || ''}|${m.text || ''}|${m.url || ''}`;
        const i = prev.findIndex((m) => !m.id && sig(m) === sig(msg));
        if (i >= 0) { const c = [...prev]; c[i] = { ...msg }; return c; }
        return [...prev, msg];
      });
    });
    es.onerror = () => { /* browser auto-reconnects (Last-Event-ID) */ };
    return () => { try { es.close(); } catch (_e) { /* noop */ } };
  }, [id, sessionId, histLoaded]);

  // Load (idempotently create) the catalogue-presentation event for this
  // collection. Stored in a dedicated backend collection — never in buyer_chats.
  useEffect(() => {
    if (!id || !sessionId) return;
    const base = process.env.REACT_APP_BACKEND_URL;
    axios
      .get(`${base}/api/collections/${id}/catalogue-event/${sessionId}`)
      .then(({ data }) => setCatEvent(focusProduct ? null : (data.event || null)))
      .catch(() => setCatEvent(null));
  }, [id, sessionId, focusProduct]);

  // Catalogue "Order this" → show ONLY the selected product as a single card
  // (reusing the existing chat product-card gallery) and pre-fill the composer
  // with the standard order-intent message. The full catalogue is suppressed
  // above, so the entire collection is never sent into the chat. Runs once,
  // after history has loaded, so it is not overwritten by the history fetch.
  useEffect(() => {
    if (!focusProduct || !histLoaded || !data || focusInjectedRef.current) return;
    const p = (data.products || []).find((x) => x.product_code === focusProduct);
    if (!p) return;
    focusInjectedRef.current = true;
    const img = p.processed_image_url || (Array.isArray(p.images) && p.images[0]) || p.original_image_url || null;
    setChatMessages((prev) => {
      if (prev.some((m) => m.id === `focus-${focusProduct}`)) return prev;
      return [...prev, {
        id: `focus-${focusProduct}`, role: 'seller',
        products: [{ product_code: p.product_code, image: img, price: p.price, moq: p.moq, colors: [] }],
        at: new Date().toISOString(),
      }];
    });
    setDraft(`I want to order ${focusProduct}`);
  }, [focusProduct, histLoaded, data]);

  // Auto-scroll to the latest bubble as content or chat updates.
  useEffect(() => {
    if (!loading && data) {
      requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ behavior: 'auto' }));
    }
  }, [loading, data, chatMessages, sending]);

  // Register service worker for buyer web push (best-effort, non-blocking).
  useEffect(() => { ensureServiceWorker().catch(() => {}); }, []);

  // Register the buyer↔seller relationship the moment the catalogue link opens.
  useEffect(() => {
    if (!id || !sessionId) return;
    const base = process.env.REACT_APP_BACKEND_URL;
    axios.post(`${base}/api/buyer/connect`, { buyer_id: sessionId, cid: id })
      .then(({ data }) => setConnection(data.connection))
      .catch(() => {});
  }, [id, sessionId]);

  // Capture the real PWA install event (shared infra) + reflect installs.
  useEffect(() => {
    const onAvail = () => setInstallPrompt(window.__fabInstallPrompt || null);
    if (window.__fabInstallPrompt) setInstallPrompt(window.__fabInstallPrompt);
    window.addEventListener('fab-install-available', onAvail);
    const onInstalled = () => {
      onboardDecidedRef.current = true; setShowOnboarding(false);
      try { axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/buyer/install-state`, { buyer_id: sessionId, state: 'installed' }); } catch (_e) {}
    };
    window.addEventListener('appinstalled', onInstalled);
    return () => { window.removeEventListener('fab-install-available', onAvail); window.removeEventListener('appinstalled', onInstalled); };
  }, [sessionId]);

  // Ticket 275862: "installed" is judged from the LIVE display mode, not the
  // stored flag. The web gives no uninstall event, so a stored 'installed'
  // outlives an uninstall and used to hide the prompt forever. A buyer's
  // "Send Notification" choice ('notify') is still honoured, so nobody who
  // already chose is re-prompted.
  const _isStandalone = () => window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  const _hasDecided = (conn, standalone) => standalone || conn.onboarding_decision === 'notify';

  // Keep the stored install_state in step with the live install state.
  useEffect(() => {
    if (!connection || !sessionId) return;
    const standalone = _isStandalone();
    const stored = connection.install_state === 'installed';
    if (standalone === stored) return;
    const state = standalone ? 'installed' : 'not_installed';
    axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/buyer/install-state`, { buyer_id: sessionId, state }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection, sessionId]);

  // Deterministic, NON-blocking onboarding trigger: 12s after a successful
  // catalogue+connect, unless the buyer already decided or runs the installed PWA.
  useEffect(() => {
    if (!connection || onboardArmedRef.current) return;
    if (_hasDecided(connection, _isStandalone())) { onboardDecidedRef.current = true; return; }
    onboardArmedRef.current = true;
    const t = setTimeout(() => { if (!onboardDecidedRef.current) setShowOnboarding(true); }, 12000);
    return () => clearTimeout(t);
  }, [connection]);

  const maybeShowOnboarding = () => {
    if (onboardDecidedRef.current || showOnboarding || !connection) return;
    if (!_hasDecided(connection, _isStandalone())) setShowOnboarding(true);
  };

  const handleBuyerInstall = async () => {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (installPrompt && installPrompt.prompt) {
      try {
        installPrompt.prompt();
        const choice = await installPrompt.userChoice;
        window.__fabInstallPrompt = null; setInstallPrompt(null);
        if (choice && choice.outcome === 'accepted') {
          onboardDecidedRef.current = true; setShowOnboarding(false);
          try { await axios.post(`${base}/api/buyer/install-state`, { buyer_id: sessionId, state: 'installed' }); } catch (_e) {}
          return 'installed';
        }
        return 'dismissed';
      } catch (_e) { return 'dismissed'; }
    }
    return 'unsupported';
  };

  const handleBuyerNotify = async () => {
    const base = process.env.REACT_APP_BACKEND_URL;
    // Kick the permission prompt off FIRST, synchronously, while the browser
    // still considers us inside the tap. Awaiting the preference POST before
    // this consumed the user activation, so Chrome on Android rejected
    // Notification.requestPermission() ("not requested from a user gesture")
    // and the whole subscribe silently aborted in the catch below — leaving the
    // buyer with a saved 'notify' preference but NO push subscription.
    const pushDone = subscribeToPush().catch(() => {});
    try {
      if (connection?.seller_id) {
        await axios.post(`${base}/api/buyer/notify-preference`, { buyer_id: sessionId, seller_id: connection.seller_id, enabled: true });
        setConnection((c) => (c ? { ...c, onboarding_decision: 'notify', collection_notifications: true } : c));
      }
    } catch (_e) {}
    try { await pushDone; } catch (_e) {}
    onboardDecidedRef.current = true; setShowOnboarding(false);
  };

  // ── Fetch seller rating (buyer-facing) — Feb 2026 rating spec §3 ──
  const [sellerRating, setSellerRating] = useState(null);
  useEffect(() => {
    const sid = data?.seller?.id || data?.seller?.seller_id;
    if (!sid) return;
    const base = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${base}/api/sellers/${sid}/rating`).then(({ data: r }) => setSellerRating(r)).catch(() => {});
  }, [data]);

  // Listen for chat-refresh events from child bubbles (e.g. after payment proof)
  useEffect(() => {
    const handler = (e) => {
      if (Array.isArray(e.detail)) {
        e.detail.forEach((m) => { if (m.id) seenIdsRef.current.add(m.id); });
        setChatMessages(e.detail);
      }
    };
    window.addEventListener('fabtexs-chat-refresh', handler);
    return () => window.removeEventListener('fabtexs-chat-refresh', handler);
  }, []);

  // Idempotent — safe to call after every order and on every load. Delegates
  // to the shared helper so the buyer and seller paths cannot drift apart.
  // Prompts only when invoked from a real user gesture.
  const subscribeToPush = () =>
    syncBuyerPush({ buyerId: sessionId, requestPermission: true });

  const sendMessage = async (overrideText) => {
    const text = (typeof overrideText === 'string' ? overrideText : draft).trim();
    if (!text || sending) return;
    setSending(true);
    setDraft('');
    // Sending a message collapses the catalogue back to the compact preview
    // (the buyer can re-expand it any time by tapping the compact block).
    setCatExpanded(false);
    // Optimistic UI — show the buyer bubble immediately.
    setChatMessages((prev) => [...prev, { role: 'buyer', text, at: new Date().toISOString() }]);
    const base = process.env.REACT_APP_BACKEND_URL;
    // Best-effort diagnostic beacon — never throws, never blocks the UI. Added
    // after a "customer's confirmation never reached us" incident where the
    // only trace of the failure was this generic catch block.
    const reportSendFailure = (err, attempt) => {
      try {
        axios.post(`${base}/api/collections/${id}/chat/client-error`, {
          session_id: sessionId,
          message_preview: text.slice(0, 200),
          error_code: err?.code || null,
          status_code: err?.response?.status ?? null,
          error_message: String(err?.message || err),
          attempt,
          at_client: new Date().toISOString(),
        }).catch(() => {});
      } catch (_e) { /* noop */ }
      // eslint-disable-next-line no-console
      console.error('[buyer-chat] send failed', { attempt, status: err?.response?.status, code: err?.code, message: err?.message });
    };
    // A response-less error (network drop, edge timeout, CORS) is worth one
    // silent retry — the buyer never sees it as long as the retry lands. A
    // response WITH a status code (validation error, 5xx) is not retried
    // blindly since the same request will just fail again the same way.
    const isRetryableNetworkError = (err) => !err?.response;
    const postChat = () => axios.post(`${base}/api/collections/${id}/chat`, {
      session_id: sessionId,
      message: text,
    });
    try {
      let r;
      try {
        ({ data: r } = await postChat());
      } catch (err) {
        reportSendFailure(err, 1);
        if (!isRetryableNetworkError(err)) throw err;
        await new Promise((res) => setTimeout(res, 1200));
        try {
          ({ data: r } = await postChat());
        } catch (err2) {
          reportSendFailure(err2, 2);
          throw err2;
        }
      }
      // Register server ids so the SSE stream won't re-append these.
      if (r.user_msg_id) seenIdsRef.current.add(r.user_msg_id);
      if (r.reply_id) seenIdsRef.current.add(r.reply_id);
      if (r.payment_instructions?.id) seenIdsRef.current.add(r.payment_instructions.id);
      // Idempotent render: the SSE stream may have already appended this reply
      // (POST vs ~1.5s SSE tick race). Append only if not already present, so
      // POST-first and SSE-first both yield exactly ONE bubble.
      setChatMessages((prev) => {
        if (r.reply_id && prev.some((m) => m.id === r.reply_id)) return prev;
        return [...prev, { id: r.reply_id, role: 'seller', text: r.reply, order_card: r.order_card || null, products: r.products || null, at: r.at || new Date().toISOString() }];
      });
      // Surface the structured QR / payment-instructions bubble live (AI order or
      // payment re-request) using the existing PaymentInstructionsBubble renderer —
      // no page refresh needed.
      if (r.payment_instructions) {
        const pb = r.payment_instructions;
        setChatMessages((prev) => {
          if (pb.id && prev.some((m) => m.id === pb.id)) return prev;
          return [...prev, { id: pb.id, role: 'seller', kind: 'payment_instructions', instructions: pb.instructions, amount: pb.amount, order_id: pb.order_id, order_number: pb.order_number, at: pb.at || r.at || new Date().toISOString() }];
        });
      }
    } catch (err) {
      // Already reported by reportSendFailure() above at the point of failure
      // (attempt 1 for a non-retryable error, attempt 2 after a failed retry).
      const text2 = err?.response?.status === 429
        ? "You're sending messages a bit fast — please wait a moment and try again."
        : "Sorry, I couldn't send that — please check your connection and try again.";
      setChatMessages((prev) => [...prev, { role: 'seller', text: text2, at: new Date().toISOString() }]);
    } finally {
      setSending(false);
      // Return focus to the composer so the buyer can keep typing.
      requestAnimationFrame(() => inputRef.current?.focus());
      // Approved trigger point — surface onboarding after a chat interaction.
      setTimeout(maybeShowOnboarding, 800);
    }
  };

  
const onKeyDown = (e) => {
  if (e.key === 'Enter' && e.shiftKey) {
    e.preventDefault();
    sendMessage();
  }
};


  // ── Attachment / camera / voice ──────────────────────────────────────
  const uploadAttachment = async (file, kind) => {
    if (!file) return;
    const now = new Date().toISOString();
    // Local optimistic preview using an object URL
    let previewUrl = null;
    try { previewUrl = URL.createObjectURL(file); } catch (_e) { /* preview unavailable */ }
    setChatMessages((prev) => [...prev, { role: 'buyer', kind, url: previewUrl, filename: file.name, at: now, uploading: true }]);
    try {
      const fd = new FormData();
      fd.append('session_id', sessionId);
      fd.append('kind', kind);
      fd.append('file', file, file.name || `${kind}-${Date.now()}`);
      const base = process.env.REACT_APP_BACKEND_URL;
      const { data: r } = await axios.post(`${base}/api/collections/${id}/chat/attach`, fd);
      if (r.id) seenIdsRef.current.add(r.id);
      // Replace the last optimistic entry with the persisted one
      setChatMessages((prev) => {
        const copy = [...prev];
        for (let i = copy.length - 1; i >= 0; i--) {
          if (copy[i].uploading) { copy[i] = { id: r.id, role: 'buyer', kind, url: r.url, filename: file.name, at: r.at }; break; }
        }
        return copy;
      });
      // Ticket 266314 (#7): fixed, no-AI reply to buyer media.
      if (r.reply) {
        if (r.reply_id) seenIdsRef.current.add(r.reply_id);
        setChatMessages((prev) => [...prev, { id: r.reply_id, role: 'seller', text: r.reply, at: r.at }]);
      }
    } catch (err) {
      setChatMessages((prev) => [...prev, { role: 'seller', text: "Sorry, that attachment couldn't be sent.", at: new Date().toISOString() }]);
    }
  };

  const onPickFile = (ref, kind) => () => {
    setAttachOpen(false);
    ref.current?.click();
    ref.current && (ref.current.onchange = (e) => {
      const f = e.target.files && e.target.files[0];
      if (f) uploadAttachment(f, kind);
      e.target.value = '';
    });
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      recChunksRef.current = [];
      recCancelledRef.current = false;
      rec.ondataavailable = (e) => { if (e.data.size) recChunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        if (recCancelledRef.current || recChunksRef.current.length === 0) return;
        const blob = new Blob(recChunksRef.current, { type: 'audio/webm' });
        const file = new File([blob], `voice-${Date.now()}.webm`, { type: 'audio/webm' });
        uploadAttachment(file, 'audio');
      };
      rec.start();
      mediaRecRef.current = rec;
      setRecStart(Date.now());
      setRecording(true);
    } catch (err) {
      alert('Microphone permission is required to record a voice message.');
    }
  };
  const stopRecording = () => {
    if (mediaRecRef.current && mediaRecRef.current.state !== 'inactive') {
      mediaRecRef.current.stop();
    }
    setRecording(false);
  };

  const confirmOrder = async (card, buyerPhone, buyerName) => {
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${base}/api/collections/${id}/chat/order`, {
        session_id: sessionId,
        product_code: card.product_code,
        quantity: card.quantity,
        buyer_name: buyerName || null,
        buyer_phone: buyerPhone,
      });
      // Refetch history — backend has just pushed order_placed +
      // payment_instructions bubbles into the persistent chat.
      try {
        const { data: hist } = await axios.get(`${base}/api/collections/${id}/chat/${sessionId}`);
        if (Array.isArray(hist.messages)) {
          hist.messages.forEach((m) => { if (m.id) seenIdsRef.current.add(m.id); });
          setChatMessages(hist.messages);
        }
      } catch (_e) { /* transient — bubbles will arrive on next reload */ }
      setOrderModal(null);
      // Politely ask for notification permission (best-effort, non-blocking)
      subscribeToPush().catch(() => {});
    } catch (err) {
      alert('Could not place the order. Please try again.');
    }
  };

  // Live-elapsed recording timer (shows mm:ss in the recording pill)
  const [recElapsed, setRecElapsed] = useState(0);
  useEffect(() => {
    if (!recording) { setRecElapsed(0); return; }
    const t = setInterval(() => setRecElapsed(Math.floor((Date.now() - recStart) / 1000)), 200);
    return () => clearInterval(t);
  }, [recording, recStart]);
  const cancelRecording = () => {
    recCancelledRef.current = true;
    if (mediaRecRef.current && mediaRecRef.current.state !== 'inactive') {
      try { mediaRecRef.current.stop(); } catch (_e) { /* already stopped */ }
    }
    setRecording(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#efe7dc]" data-testid="collection-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#25D366]" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#efe7dc] p-6">
        <div className="bg-white rounded-2xl p-6 max-w-md w-full text-center shadow" data-testid="collection-error">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto" />
          <div className="font-semibold text-[16px] mt-3">Collection not found</div>
          <div className="text-[13px] text-neutral-600 mt-1">{error}</div>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const sellerName = data.seller?.business_name || data.seller?.name || 'FABTEXS Seller';
  const sellerPhone = (data.seller?.phone || '').trim();
  const personaName = (data.seller?.ai_persona_name || '').trim();
  const catList = Array.isArray(data.categories) ? data.categories.filter(Boolean) : [];
  const catPhrase = catList.length
    ? `We currently have ${catList.slice(0, 4).join(', ')}${catList.length > 4 ? ' and more' : ''} available.`
    : '';
  const initials = (sellerName || 'FS').split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  const products = data.products || [];
  const description = (data.description || '').trim();
  // Catalogue-event driven presentation. The event (dedicated store) carries an
  // immutable product snapshot. Before the buyer interacts, images render
  // vertically; once the buyer has replied (any message at/after the event was
  // opened), the SAME snapshot consolidates IN PLACE into one album block — no
  // images are resent. A returning buyer with pre-existing history gets no event
  // (event=null) so their conversation simply continues.
  const catProducts = (catEvent?.products || []).filter((p) => p.image_url);
  const catDescription = (catEvent?.description || '').trim();
  

const downloadAllCatalogueImages = async () => {
  const productsToDownload = catProducts.filter((p) => p.image_url);

  if (!productsToDownload.length) return;

  for (let i = 0; i < productsToDownload.length; i++) {
    const product = productsToDownload[i];

    try {
      const response = await fetch(product.image_url);
      if (!response.ok) {
        throw new Error("Image download failed");
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `${product.product_code || `image-${i + 1}`}.jpg`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      setTimeout(() => URL.revokeObjectURL(url), 10000);
      await new Promise((resolve) => setTimeout(resolve, 300));
    } catch (error) {
      console.error("Failed to download catalogue image:", product.image_url, error);
    }
  }
};
  const openedAtMs = catEvent ? new Date(catEvent.opened_at || 0).getTime() : 0;
  const buyerInteracted = !!catEvent && chatMessages.some(
    (m) => new Date(m.at || 0).getTime() >= openedAtMs,
  );
  // Expanded when it's the first-time view OR the buyer tapped the compact
  // block to reopen the full catalogue. Sending a message collapses it again.
  const showFullCatalogue = !!catEvent && (!buyerInteracted || catExpanded);
  const dateLabel = data.created_at
    ? new Date(data.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })
    : new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
  const nowTime = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
  // Chronological placement of the catalogue-presentation event: it belongs
  // AFTER any messages that predate it (e.g. a prior conversation the SAME
  // buyer had on another collection) and BEFORE messages that follow. This
  // keeps existing history intact and in order — opening a NEW catalogue link
  // never reorders, duplicates or resets prior chat.
  const catInsertIdx = catEvent
    ? chatMessages.filter((m) => new Date(m.at || 0).getTime() < openedAtMs).length
    : -1;
  const catalogueBlock = catEvent ? (
    <>
      {/* Welcome bubble (incoming — left side, white) */}
      <ChatBubble side="in" testid="chat-welcome" time={nowTime}>
        <div className="whitespace-pre-line">
          {`👋 Hi! Welcome to ${sellerName} 😊
I'm ${personaName ? `${personaName}, your AI assistant` : 'your AI assistant'}.

✨ Here's our latest collection for you.${catPhrase ? `\n${catPhrase}` : ''}
You can browse the designs below or simply tell me what you're looking for.

💬 You can ask me things like:
• Show me what you have
• What is the price of a product?
• I want this design
• Show me available designs`}
        </div>
      </ChatBubble>

      {/* BEFORE buyer interaction: full vertical catalogue (all snapshot
          products). AFTER interaction: the SAME snapshot consolidates IN
          PLACE into one album block — images are never resent. */}
      {showFullCatalogue ? (
    <>
    
      {catProducts.map((p, idx) => {
          const src = p.image_url;
          const alt = p.product_code || `Product ${idx + 1}`;
          return (
            <ChatBubble
              key={p.product_id || idx}
              side="in"
              testid={`chat-product-${idx + 1}`}
              time={nowTime}
              media
            >
              <button
                type="button"
                onClick={() => setImgView({ src, alt })}
                className="block p-0 border-0 bg-transparent cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-fab-primary rounded-md"
                aria-label={`View ${alt} full size`}
                data-testid={`product-image-tap-${idx + 1}`}
              >
                <img
                  src={src}
                  alt={alt}
                  className="w-full max-w-[280px] sm:max-w-[320px] h-auto rounded-md object-cover"
                  loading="lazy"
                />
              </button>
              {p.product_code && (
                <button
                  type="button"
                  onClick={() => {
                    setDraft(`Tell me about ${p.product_code}`);
                    requestAnimationFrame(() => {
                      inputRef.current?.focus();
                      const el = inputRef.current;
                      if (el && typeof el.setSelectionRange === 'function') {
                        const len = el.value.length;
                        el.setSelectionRange(len, len);
                      }
                    });
                  }}
                  className="mt-1.5 inline-flex items-center gap-1 text-[12px] font-medium text-fab-primary hover:text-fab-primary-dark active:scale-[0.98] px-2.5 py-1 rounded-full border border-fab-primary/25 bg-fab-primary/5"
                  data-testid={`product-ask-${idx + 1}`}
                  aria-label={`Ask about ${p.product_code}`}
                >
                  💬 Ask about {p.product_code}
                </button>
              )}
            </ChatBubble>
          );
        })}
      </>
      ) : (
        <ChatBubble side="in" testid="chat-catalogue-block" media>
          <button
            type="button"
            onClick={() => setCatExpanded(true)}
            data-testid="catalogue-block-expand"
            className="block w-full text-left p-0 border-0 bg-transparent cursor-pointer focus:outline-none focus:ring-2 focus:ring-fab-primary rounded-md"
            aria-label={`View all ${catProducts.length} designs`}
          >
            <div className="text-[12px] font-medium text-neutral-600 mb-1.5">
              📸 Catalogue · {catProducts.length} {catProducts.length === 1 ? 'design' : 'designs'}
            </div>
            <div className="grid grid-cols-2 gap-1 max-w-[300px]" data-testid="catalogue-block-grid">
              {catProducts.slice(0, 4).map((p, idx) => {
                const src = p.image_url;
                const alt = p.product_code || `Product ${idx + 1}`;
                const isLastTile = idx === 3 && catProducts.length > 4;
                const remaining = catProducts.length - 4;
                return (
                  <div
                    key={p.product_id || idx}
                    className="relative rounded-md overflow-hidden"
                    data-testid={`catalogue-block-tile-${idx + 1}`}
                  >
                    <img src={src} alt={alt} className="w-full aspect-square object-cover" loading="lazy" />
                    {isLastTile && (
                      <div
                        className="absolute inset-0 bg-black/55 flex items-center justify-center text-white text-[18px] font-bold"
                        data-testid="catalogue-block-more"
                      >
                        +{remaining}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-1.5 text-[12px] font-medium text-fab-primary">Tap to view all {catProducts.length} designs →</div>
          </button>
        </ChatBubble>
      )}

      {/* Final bubble: raw seller collection description — verbatim snapshot */}
      {catDescription && (
        <ChatBubble side="in" testid="chat-description" time={nowTime}>
          <div className="whitespace-pre-line">{catDescription}</div>
        </ChatBubble>
      )}
    </>
  ) : null;


  return (
    <div className="min-h-screen min-h-[100dvh] bg-[#efe7dc] flex flex-col" data-testid="collection-chat-view">
      <BuyerOnboardingPrompt
        open={showOnboarding}
        installPrompt={installPrompt}
        onInstall={handleBuyerInstall}
        onNotify={handleBuyerNotify}
        onClose={() => setShowOnboarding(false)}
      />
      {/* Buyer alert layer: polls the buyer-scoped feed so an OPEN app on the
          catalogue/chat screen actually alerts (it previously only did on /me),
          and keeps the web-push subscription alive for the closed/locked cases.
          Headless — renders nothing, never blocks the chat. */}
      <BuyerNotificationAlerts
        buyerId={sessionId}
        onOpen={(n) => { if (n && n.url) nav(n.url); }}
      />
      {/* ── WhatsApp-style header ─────────────────────────────────────── */}
      <header
        className="sticky top-0 z-20 bg-[#f7f2eb]/95 backdrop-blur-md border-b border-black/10 flex items-center gap-2 px-2 sm:px-3 py-2 sm:py-2.5"
        data-testid="chat-header"
      >
        <button
          onClick={() => {
            // Buyers using the installed Buyer app go back to their "My Sellers"
            // screen. We detect an app-buyer via the SAME Step-2 signals:
            //   • the chosen Buyer role (fabtexs.role), OR
            //   • an active seller set by tapping a seller in My Sellers
            //     (fabtexs.active_seller) — covers installed buyers who reached
            //     /me via their stored buyer id without re-passing the role gate.
            // Public WhatsApp-link visitors have neither, so they keep the
            // existing behaviour (marketing home).
            let isAppBuyer = false;
            try {
              isAppBuyer = localStorage.getItem('fabtexs.role') === 'buyer'
                || !!localStorage.getItem('fabtexs.active_seller');
            } catch (_e) { /* private mode */ }
            nav(isAppBuyer ? '/me' : '/');
          }}
          className="p-2 -ml-1 rounded-full hover:bg-black/5"
          aria-label="Back"
          data-testid="chat-back"
        ><ArrowLeft className="w-5 h-5 text-neutral-800" /></button>
        <div className="relative shrink-0">
          <div className="w-11 h-11 rounded-full bg-[#25D366] text-white font-bold flex items-center justify-center text-[15px] shadow-inner ring-2 ring-white">
            {initials}
          </div>

        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <div className="font-semibold text-[15px] sm:text-[16px] text-neutral-900 truncate" data-testid="chat-seller-name">
              
<button
  type="button"
  onClick={() => setSellerDetailsOpen(true)}
  className="text-left hover:underline cursor-pointer"
  aria-label="View seller details"
>
  {sellerName}
</button>
            </div>
            {/* Verified blue check */}
            <svg viewBox="0 0 24 24" className="w-4 h-4 text-[#1DA1F2] shrink-0" aria-hidden="true">
              <path fill="currentColor" d="M23 12l-2.44-2.79.34-3.69-3.61-.82-1.89-3.2L12 2.96 8.6 1.5 6.71 4.69 3.1 5.5l.34 3.7L1 12l2.44 2.79-.34 3.7 3.61.82L8.6 22.5 12 21.04l3.4 1.46 1.89-3.19 3.61-.82-.34-3.7L23 12zm-13 5l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
            </svg>
          </div>
          <div className="text-[12px] text-neutral-600 -mt-0.5 flex items-center gap-1.5">
            <span>Business Account</span>
            {sellerRating && sellerRating.count > 0 && (
              <span className="inline-flex items-center gap-0.5 text-neutral-700" data-testid="chat-seller-rating">
                <span className="text-neutral-400">·</span>
                <span className="text-amber-500">★</span>
                <span className="font-semibold">{sellerRating.average}</span>
                <span className="text-neutral-500">({sellerRating.count})</span>
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 text-neutral-700">
          <button
            className="p-2 rounded-full hover:bg-black/5"
            aria-label="Catalog"
            data-testid="chat-catalog-btn"
            onClick={() => {
              // Feb 2026 fix — open the seller's overall customer-facing
              // catalogue (all published collections) instead of scrolling
              // within the current collection page.
              const sid = data?.seller?.id;
              if (sid) nav(`/s/${sid}`);
            }}
          ><Store className="w-5 h-5" /></button>
          {data.seller?.instagram_url && (
            <a
              href={data.seller.instagram_url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-full hover:bg-black/5 inline-flex"
              aria-label="Instagram"
              data-testid="chat-instagram-btn"
            >
              {/* Feb 2026 — recognizable Instagram gradient logo (inline SVG,
                  no external asset, matches attached reference). Kept at the
                  same 20×20 size (w-5 h-5) as the sibling Catalog icon. */}
              <svg
                viewBox="0 0 24 24"
                className="w-5 h-5"
                aria-hidden="true"
                focusable="false"
              >
                <defs>
                  <radialGradient
                    id="ig-grad"
                    cx="0.3"
                    cy="1"
                    r="1.1"
                    gradientUnits="objectBoundingBox"
                  >
                    <stop offset="0"    stopColor="#FDCB5C" />
                    <stop offset="0.25" stopColor="#F17A2C" />
                    <stop offset="0.5"  stopColor="#E1306C" />
                    <stop offset="0.75" stopColor="#C13584" />
                    <stop offset="1"    stopColor="#833AB4" />
                  </radialGradient>
                </defs>
                <rect x="1.5" y="1.5" width="21" height="21" rx="6" fill="url(#ig-grad)" />
                <rect
                  x="6" y="6" width="12" height="12" rx="3.5"
                  fill="none" stroke="#fff" strokeWidth="1.7"
                />
                <circle
                  cx="12" cy="12" r="3.3"
                  fill="none" stroke="#fff" strokeWidth="1.7"
                />
                <circle cx="17.2" cy="6.8" r="0.95" fill="#fff" />
              </svg>
            </a>
          )}
          <button className="p-2 rounded-full hover:bg-black/5" aria-label="Menu" data-testid="chat-menu-btn"><MoreVertical className="w-5 h-5" /></button>
        </div>
      </header>
      
      {/* Seller Details Popup */}
      {sellerDetailsOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setSellerDetailsOpen(false)}
        >
          <div
            className="relative w-full max-w-sm rounded-xl bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="seller-details-title"
          >
            <button
              type="button"
              onClick={() => setSellerDetailsOpen(false)}
              className="absolute right-3 top-3 rounded-full p-2 text-gray-600 hover:bg-gray-100"
              aria-label="Close seller details"
            >
              ✕
            </button>

            <h2
              id="seller-details-title"
              className="mb-4 pr-8 text-lg font-semibold text-gray-900"
            >
              Seller Details
            </h2>

            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <p className="text-xs text-gray-500">Seller Name</p>
                <p className="font-medium">{sellerName}</p>
              </div>

              {sellerPhone ? (
                <div>
                  <p className="text-xs text-gray-500">Contact Number</p>
                  <a
                    href={`tel:${sellerPhone}`}
                    className="font-medium text-blue-700 hover:underline"
                  >
                    {sellerPhone}
                  </a>
                </div>
              ) : (
                <p className="text-gray-500">
                  Contact number is not available.
                </p>
              )}
            </div>
          </div>
        </div>
      )}


      {/* ── Chat area ─────────────────────────────────────────────────── */}
      <main
        className="flex-1 overflow-y-auto px-2 sm:px-3 py-4 space-y-2"
        style={{ backgroundColor: '#efe7dc' }}
        data-testid="chat-body"
      >
        {/* Date pill */}
        <div className="flex justify-center">
          <div className="text-[11px] font-medium text-neutral-700 bg-white/85 rounded-full px-3 py-1 shadow-sm" data-testid="chat-date-pill">
            {dateLabel}
          </div>
        </div>

        {/* Encryption notice (WhatsApp-style pale-yellow) */}
        <div className="flex justify-center">
          <div className="max-w-[85%] bg-[#fff5c2] rounded-lg px-3 py-2 text-[12px] text-neutral-800 shadow-sm text-center leading-snug">
            🔒 Messages and calls are end-to-end encrypted. No one outside of this chat can read or listen to them.
          </div>
        </div>

        {/* Catalogue-presentation event is rendered chronologically
            inside the message list below (see catalogueBlock). */}


        {/* ── Live conversation (v2) — buyer ↔ AI. The catalogue event is
            placed at its chronological position: messages older than it render
            above, the catalogue block renders here, newer messages below. This
            keeps prior history intact and never reorders/duplicates it. ── */}
        {chatMessages.flatMap((m, i) => [
          (catEvent && i === catInsertIdx) ? (
            <React.Fragment key={`cat-${i}`}>{catalogueBlock}</React.Fragment>
          ) : null,
          (() => {
          const side = m.role === 'buyer' ? 'out' : 'in';
          const time = new Date(m.at || Date.now()).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
          const isBuyer = m.role === 'buyer';
          // ── NEW: commerce bubbles (payment / dispatch) ───────────────
          if (m.kind === 'payment_instructions') {
            // Sep 2026 (ticket 240848) -- once a later message for this same
            // order shows payment/shipping/delivery/rating progressed past
            // proof-submission, this card's action buttons must stop being
            // actionable (the backend now rejects a resubmit here too; this
            // is the frontend-visible counterpart, computed from data already
            // in `chatMessages` -- no new fetch, no new backend field).
            const superseded = chatMessages.slice(i + 1).some((x) => x.order_id === m.order_id && [
  'payment_confirmed', 'payment_rejected', 'order_shipped',
  'order_dispatched', 'parcel_received_prompt', 'rate_request', 'rating_thanks',
  'payment_proof', 'payment_proof_ack',
].includes(x.kind));
            return <PaymentInstructionsBubble
                     key={`msg-${i}`} idx={i} time={time} data={m} superseded={superseded}
                     backendUrl={process.env.REACT_APP_BACKEND_URL}
                     collectionId={id} sessionId={sessionId} />;
          }
          if (m.kind === 'payment_confirmed') {
            return (
              <ChatBubble key={`msg-${i}`} side="in" testid={`chat-msg-payment-confirmed-${i}`} time={time}>
                <div className="whitespace-pre-line text-[13.5px] text-neutral-800">{m.text}</div>
              </ChatBubble>
            );
          }
          if (m.kind === 'payment_rejected') {
            return (
              <ChatBubble key={`msg-${i}`} side="in" testid={`chat-msg-payment-rejected-${i}`} time={time}>
                <div className="text-[13.5px] text-neutral-800 whitespace-pre-line">{m.text}</div>
              </ChatBubble>
            );
          }
          if (m.kind === 'payment_proof') {
            return (
              <ChatBubble key={`msg-${i}`} side="out" testid={`chat-msg-payment-proof-${i}`} time={time} outgoing>
                {m.screenshot_url && (
                  <img src={m.screenshot_url.startsWith('http') ? m.screenshot_url : `${process.env.REACT_APP_BACKEND_URL}/api/collections/${id}/chat/${sessionId}/payment-screenshot/${m.order_id}?k=${encodeURIComponent(m.screenshot_url)}`}
                       alt="Payment screenshot" className="w-full max-w-[240px] rounded-md mb-1" />
                )}
                <div className="text-[13px] whitespace-pre-line">{m.text || 'Payment done'}</div>
              </ChatBubble>
            );
          }
          if (m.kind === 'payment_proof_ack' || m.kind === 'order_placed') {
            return (
              <ChatBubble key={`msg-${i}`} side="in" testid={`chat-msg-${m.kind}-${i}`} time={time}>
                <div className="text-[13.5px] text-neutral-800 whitespace-pre-line">{m.text}</div>
              </ChatBubble>
            );
          }
          if (m.kind === 'order_dispatched') {
            const parcelUrl = m.parcel_image_url && (m.parcel_image_url.startsWith('http')
              ? m.parcel_image_url
              : `${process.env.REACT_APP_BACKEND_URL}${m.parcel_image_url}`);
            return (
              <ChatBubble key={`msg-${i}`} side="in" testid={`chat-msg-dispatched-${i}`} time={time} media>
                {parcelUrl && (
                  <img src={parcelUrl} alt="Parcel dispatched" data-testid={`parcel-image-${i}`}
                       className="w-full max-w-[280px] sm:max-w-[320px] rounded-md object-cover mb-2" />
                )}
                <div className="text-[13.5px] text-neutral-800 whitespace-pre-line">{m.text}</div>
              </ChatBubble>
            );
          }
          if (m.kind === 'order_shipped') {
            const parcelUrl = m.parcel_image_url && (m.parcel_image_url.startsWith('http')
              ? m.parcel_image_url
              : `${process.env.REACT_APP_BACKEND_URL}${m.parcel_image_url}`);
            return (
              <ChatBubble key={`msg-${i}`} side="in" testid={`chat-msg-shipped-${i}`} time={time} media>
                {parcelUrl && (
                  <img src={parcelUrl} alt="Parcel shipped" data-testid={`shipped-parcel-image-${i}`}
                       className="w-full max-w-[280px] sm:max-w-[320px] rounded-md object-cover mb-2" />
                )}
                <div className="text-[13.5px] text-neutral-800 whitespace-pre-line">{m.text}</div>
                {m.tracking_url && (
                  <a href={m.tracking_url} target="_blank" rel="noopener noreferrer"
                     data-testid={`track-link-${i}`}
                     className="inline-block mt-2 text-[13px] font-semibold text-[#075E54] underline">
                    Track your parcel
                  </a>
                )}
              </ChatBubble>
            );
          }
          if (m.kind === 'parcel_received_prompt') {
            const advanced = chatMessages.some(
              (x) => (x.kind === 'rate_request' || x.kind === 'rating_thanks') && x.order_id === m.order_id);
            return <ParcelReceivedBubble
                     key={`msg-${i}`} idx={i} time={time} data={m} done={advanced}
                     backendUrl={process.env.REACT_APP_BACKEND_URL}
                     collectionId={id} sessionId={sessionId} />;
          }
          if (m.kind === 'rate_request') {
            const thanksMsg = chatMessages.find(
              (x) => x.kind === 'rating_thanks' && x.order_id === m.order_id);
            return <RateRequestBubble
                     key={`msg-${i}`} idx={i} time={time} data={m} rated={!!thanksMsg} ratedValue={thanksMsg?.rating}
                     backendUrl={process.env.REACT_APP_BACKEND_URL}
                     collectionId={id} sessionId={sessionId} />;
          }
          if (m.kind === 'rating_thanks') {
            return (
              <ChatBubble key={`msg-${i}`} side="in" testid={`chat-msg-rating-thanks-${i}`} time={time}>
                <div className="text-[13.5px] text-neutral-800">{m.text}</div>
              </ChatBubble>
            );
          }
          // Media messages
          if (m.kind === 'image') {
            return (
              <ChatBubble key={`msg-${i}`} side={side} testid={`chat-msg-${m.role}-${i}`} time={time} media outgoing={isBuyer}>
                <img src={m.url} alt="attachment" className="w-full max-w-[280px] sm:max-w-[320px] h-auto rounded-md object-cover" />
              </ChatBubble>
            );
          }
          if (m.kind === 'video') {
            return (
              <ChatBubble key={`msg-${i}`} side={side} testid={`chat-msg-${m.role}-${i}`} time={time} media outgoing={isBuyer}>
                <video src={m.url} controls className="w-full max-w-[280px] sm:max-w-[320px] rounded-md" />
              </ChatBubble>
            );
          }
          if (m.kind === 'audio') {
            return (
              <ChatBubble key={`msg-${i}`} side={side} testid={`chat-msg-${m.role}-${i}`} time={time} outgoing={isBuyer}>
                <audio src={m.url} controls className="w-[220px] sm:w-[260px]" />
              </ChatBubble>
            );
          }
          if (m.kind === 'document' || m.kind === 'contact') {
            return (
              <ChatBubble key={`msg-${i}`} side={side} testid={`chat-msg-${m.role}-${i}`} time={time} outgoing={isBuyer}>
                <a href={m.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 underline">
                  <FileText className="w-4 h-4" /> {m.filename || 'File'}
                </a>
              </ChatBubble>
            );
          }
          // Jun 2026 — TEXT FIRST, then the product gallery. The reply is the
          // introduction to the cards, so it must render ABOVE them.
          return (
            <React.Fragment key={`msg-${i}`}>
              {m.text && (
                <ChatBubble side={side} testid={`chat-msg-${m.role}-${i}`} time={time} outgoing={isBuyer}>
                  <div className="whitespace-pre-line">
                    {m.text.split('[[CATALOGUE_ICON]]').map((part, ci, arr) => (
                      <React.Fragment key={ci}>
                        {part}
                        {ci < arr.length - 1 && (
                          <Store className="inline w-4 h-4 align-text-bottom mx-0.5" aria-label="Catalogue" />
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </ChatBubble>
              )}
              {Array.isArray(m.products) && m.products.length > 0 && (
                <div className="flex justify-start" data-testid={`chat-gallery-${i}`}>
                  <div className="max-w-[92%] sm:max-w-[80%] grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {m.products.map((p, pi) => {
                      const raw = p.image;
                      const src = raw ? (raw.startsWith('http') ? raw : `${process.env.REACT_APP_BACKEND_URL}${raw}`) : null;
                      return (
                        <button
                          type="button"
                          key={p.product_code || pi}
                          onClick={() => sendMessage(`I want to order ${p.product_code}`)}
                          disabled={sending}
                          className="text-left bg-white rounded-lg shadow-sm overflow-hidden border border-neutral-200 hover:shadow-md transition-shadow disabled:opacity-60 cursor-pointer"
                          data-testid={`gallery-card-${i}-${pi}`}
                          aria-label={`Order ${p.product_code}`}
                        >
                          {src ? (
                            <img src={src} alt={p.product_code} loading="lazy" className="w-full h-28 sm:h-32 object-cover" />
                          ) : (
                            <div className="w-full h-28 sm:h-32 bg-neutral-100 flex items-center justify-center text-[11px] text-neutral-400">No image</div>
                          )}
                          <div className="p-2">
                            <div className="text-[12px] font-semibold text-neutral-900 truncate">{p.product_code}</div>
                            {Array.isArray(p.colors) && p.colors.length > 0 && (
                              <div className="text-[11px] text-neutral-500 capitalize truncate">{p.colors.slice(0, 3).join(', ')}</div>
                            )}
                            {p.price != null && (
                              <div className="text-[13px] font-bold text-[#128C7E] mt-0.5">₹{p.price}{p.moq ? ` · MOQ ${p.moq}` : ''}</div>
                            )}
                            <div
                              className="mt-1.5 w-full text-[11px] font-medium text-fab-primary px-2 py-1 rounded-full border border-fab-primary/25 bg-fab-primary/5 text-center"
                              data-testid={`gallery-order-${i}-${pi}`}
                            >
                              Order this
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {m.order_card && (
                <div className="flex justify-start">
                  <div className="max-w-[90%] sm:max-w-[80%] bg-white rounded-xl shadow-sm p-3 border-l-4 border-[#25D366]" data-testid={`order-card-${i}`}>
                    <div className="flex gap-3">
                      {m.order_card.product_image && (
                        <img src={m.order_card.product_image} className="w-16 h-16 rounded object-cover" alt="" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold text-neutral-900 truncate">{m.order_card.product_name || m.order_card.product_code}</div>
                        <div className="text-[12px] text-neutral-700 mt-0.5">Code: {m.order_card.product_code}</div>
                        <div className="text-[12px] text-neutral-700">Qty: {m.order_card.quantity}   ·   ₹{m.order_card.unit_price}/pc</div>
                        <div className="text-[12px] text-neutral-700">GST (5%): ₹{Math.round(m.order_card.gst_amount)}</div>
                        <div className="text-[14px] font-bold text-[#25D366] mt-1">Total: ₹{Math.round(m.order_card.total_amount)}</div>
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <button
                        onClick={() => setOrderModal({ card: m.order_card, msgIndex: i })}
                        className="flex-1 bg-[#25D366] text-white text-[13px] font-semibold py-2 rounded-lg"
                        data-testid={`confirm-order-${i}`}
                      >Confirm Order</button>
                    </div>
                  </div>
                </div>
              )}
            </React.Fragment>
          );
          })(),
        ])}
        {/* Fresh buyer (no history) OR catalogue newer than all messages:
            render the catalogue block after the list. */}
        {catEvent && catInsertIdx >= chatMessages.length && catalogueBlock}

        {/* WhatsApp-style typing indicator */}
        {sending && (
          <div className="flex justify-start" data-testid="chat-typing">
            <div className="bg-white rounded-lg px-3 py-2 shadow-sm flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-neutral-500 animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-neutral-500 animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-neutral-500 animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </main>

      {/* ── Functional composer (v2 — real interactive input) ──────── */}
      <footer
        className="sticky bottom-0 bg-transparent px-2 sm:px-3 pt-2 pb-3 sm:pb-4"
        data-testid="chat-composer"
      >
        {recording ? (
          <RecordingBar elapsed={recElapsed} onCancel={cancelRecording} onStop={stopRecording} />
        ) : (
          <div className="flex items-end gap-2 w-full min-w-0">
            <div className="flex-1 bg-white rounded-3xl flex items-end gap-1 px-2 py-1.5 shadow">
              <button
                type="button"
                onClick={() => setAttachOpen(true)}
                className="p-2 rounded-full hover:bg-black/5 shrink-0"
                aria-label="Attach"
                data-testid="composer-attach"
              ><Plus className="w-5 h-5 text-neutral-500" /></button>
              <textarea
                ref={inputRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Message"
                rows={1}
                className="flex-1 min-w-0 resize-none bg-transparent outline-none text-[15px] leading-6 text-neutral-900 placeholder-neutral-400 max-h-32 py-2"
                data-testid="composer-input"
                inputMode="text"
                autoComplete="off"
                aria-label="Message"
              />
              {/* Note: no emoji icon here — the buyer's native mobile keyboard
                  already provides the full emoji picker. */}
              <button
                type="button"
                onClick={() => {
                  // Use the real in-app camera (getUserMedia). If not available
                  // (e.g. old browsers, insecure context), fall back to the
                  // native OS camera picker via `capture=environment`.
                  const hasGUM = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
                  if (hasGUM) setCameraOpen(true);
                  else cameraRef.current?.click();
                }}
                className="p-2 rounded-full hover:bg-black/5 shrink-0"
                aria-label="Camera"
                data-testid="composer-camera"
              ><Camera className="w-5 h-5 text-neutral-500" /></button>
            </div>
            {draft.trim() ? (
              <button
                onClick={sendMessage}
                disabled={sending}
                className="w-11 h-11 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow disabled:opacity-60"
                aria-label="Send"
                data-testid="composer-send"
              ><Send className="w-5 h-5" /></button>
            ) : (
              <button
                onClick={startRecording}
                className="w-11 h-11 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow"
                aria-label="Record voice message"
                data-testid="composer-mic"
              ><Mic className="w-5 h-5" /></button>
            )}
          </div>
        )}
      </footer>

      {/* Hidden file inputs — driven by the attachment sheet + camera button */}
      <input ref={fileDocRef}     type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" className="hidden" data-testid="hidden-doc-input" />
      <input ref={filePhotoRef}   type="file" accept="image/*" className="hidden" data-testid="hidden-photo-input" />
      <input ref={fileVideoRef}   type="file" accept="video/*" className="hidden" data-testid="hidden-video-input" />
      <input ref={fileAudioRef}   type="file" accept="audio/*" className="hidden" data-testid="hidden-audio-input" />
      <input ref={fileContactRef} type="file" accept=".vcf,text/vcard" className="hidden" data-testid="hidden-contact-input" />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*,video/*"
        capture="environment"
        className="hidden"
        data-testid="hidden-camera-input"
        onChange={(e) => {
          const f = e.target.files && e.target.files[0];
          if (f) {
            const kind = f.type.startsWith('video/') ? 'video' : 'image';
            uploadAttachment(f, kind);
          }
          e.target.value = '';
        }}
      />

      {/* Attachment bottom-sheet (WhatsApp-style) */}
      {attachOpen && (
        <AttachmentSheet
          onClose={() => setAttachOpen(false)}
          onPick={{
            document: onPickFile(fileDocRef,     'document'),
            photo:    onPickFile(filePhotoRef,   'image'),
            video:    onPickFile(fileVideoRef,   'video'),
            audio:    onPickFile(fileAudioRef,   'audio'),
            contact:  onPickFile(fileContactRef, 'contact'),
          }}
        />
      )}

      {/* Order confirmation modal */}
      {orderModal && (
        <OrderConfirmModal
          card={orderModal.card}
          onCancel={() => setOrderModal(null)}
          onSubmit={(phone, name) => confirmOrder(orderModal.card, phone, name)}
        />
      )}

      {/* In-app camera capture (getUserMedia) — real camera preview + capture */}
      {cameraOpen && (
        <CameraCapture
          onClose={() => setCameraOpen(false)}
          onCapture={(file, kind) => { setCameraOpen(false); uploadAttachment(file, kind); }}
          onFallback={() => { setCameraOpen(false); cameraRef.current?.click(); }}
        />
      )}

      {imgView && (
        <ImageViewer
          src={imgView.src}
          alt={imgView.alt}
          onClose={() => setImgView(null)}
        />
      )}
    </div>
  );
}

/**
 * WhatsApp-style bottom sheet with 5 tile buttons (Documents, Photos, Videos,
 * Audio, Contacts).
 */
function AttachmentSheet({ onClose, onPick }) {
  const tiles = [
    { key: 'document', label: 'Document',  color: '#7F66FF', Icon: FileText,   testid: 'attach-document' },
    { key: 'photo',    label: 'Photos',    color: '#B01FA0', Icon: ImageIcon,  testid: 'attach-photo' },
    { key: 'video',    label: 'Videos',    color: '#E13B3C', Icon: Video,      testid: 'attach-video' },
    { key: 'audio',    label: 'Audio',     color: '#F97316', Icon: Music,      testid: 'attach-audio' },
    { key: 'contact',  label: 'Contact',   color: '#009DE1', Icon: User,       testid: 'attach-contact' },
  ];
  return (
    <div className="fixed inset-0 z-30 flex items-end justify-center" data-testid="attachment-sheet">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} data-testid="attach-backdrop" />
      <div className="relative w-full sm:max-w-md bg-white rounded-t-2xl shadow-2xl pt-3 pb-6 px-4 animate-[slide-up_180ms_ease-out]">
        <div className="mx-auto w-10 h-1.5 bg-neutral-300 rounded-full mb-4" />
        <div className="grid grid-cols-3 gap-y-5 gap-x-3">
          {tiles.map(({ key, label, color, Icon, testid }) => (
            <button
              key={key}
              onClick={onPick[key]}
              className="flex flex-col items-center gap-1.5 focus:outline-none"
              data-testid={testid}
            >
              <span
                className="w-14 h-14 rounded-full flex items-center justify-center shadow-md"
                style={{ backgroundColor: color }}
              >
                <Icon className="w-6 h-6 text-white" />
              </span>
              <span className="text-[12px] text-neutral-700">{label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Voice-recording pill that REPLACES the composer while a recording is in
 * flight. Shows a pulsing red dot + elapsed time + cancel (X) + send (▶).
 */
function RecordingBar({ elapsed, onCancel, onStop }) {
  const mm = String(Math.floor(elapsed / 60)).padStart(2, '0');
  const ss = String(elapsed % 60).padStart(2, '0');
  return (
    <div className="flex items-center gap-2" data-testid="recording-bar">
      <button
        onClick={onCancel}
        className="w-11 h-11 rounded-full bg-white text-neutral-700 flex items-center justify-center shadow"
        aria-label="Cancel recording"
        data-testid="recording-cancel"
      ><X className="w-5 h-5" /></button>
      <div className="flex-1 bg-white rounded-3xl shadow flex items-center gap-2 px-4 py-3">
        <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
        <span className="text-[15px] text-neutral-800 tabular-nums" data-testid="recording-timer">
          Recording · {mm}:{ss}
        </span>
      </div>
      <button
        onClick={onStop}
        className="w-11 h-11 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow"
        aria-label="Send voice"
        data-testid="recording-stop"
      ><Send className="w-5 h-5" /></button>
    </div>
  );
}

/**
 * Order confirmation modal. Buyer confirms qty, gives phone + optional name,
 * then we POST /chat/order.
 */
function OrderConfirmModal({ card, onCancel, onSubmit }) {
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const valid = /^\+?\d{10,15}$/.test(phone.replace(/\s|-/g, ''));
  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try { await onSubmit(phone.trim(), name.trim()); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-40 flex items-end sm:items-center justify-center" data-testid="order-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[16px] font-semibold text-neutral-900">Confirm your order</div>
            <div className="text-[12px] text-neutral-500 mt-0.5">The seller will contact you on this number.</div>
          </div>
          <button onClick={onCancel} className="p-1 -m-1 rounded-full hover:bg-black/5" aria-label="Close" data-testid="order-close">
            <X className="w-5 h-5 text-neutral-700" />
          </button>
        </div>
        <div className="mt-4 bg-neutral-50 rounded-xl p-3 flex gap-3">
          {card.product_image && <img src={card.product_image} className="w-14 h-14 rounded object-cover" alt="" />}
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold text-neutral-900 truncate">{card.product_name || card.product_code}</div>
            <div className="text-[12px] text-neutral-700">Code: {card.product_code} · Qty: {card.quantity}</div>
            <div className="text-[13px] font-bold text-[#25D366] mt-0.5">Total: ₹{Math.round(card.total_amount)}</div>
          </div>
        </div>
        <div className="mt-4 space-y-3">
          <div>
            <label className="text-[12px] text-neutral-600">Your name (optional)</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              className="mt-1 w-full border border-neutral-300 rounded-lg px-3 py-2 text-[14px] outline-none focus:border-[#25D366]"
              data-testid="order-name-input"
            />
          </div>
          <div>
            <label className="text-[12px] text-neutral-600">Phone (WhatsApp)</label>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g. +91 90000 00000"
              inputMode="tel"
              className="mt-1 w-full border border-neutral-300 rounded-lg px-3 py-2 text-[14px] outline-none focus:border-[#25D366]"
              data-testid="order-phone-input"
            />
          </div>
        </div>
        <button
          onClick={submit}
          disabled={!valid || busy}
          className="mt-5 w-full bg-[#25D366] text-white font-semibold py-3 rounded-xl disabled:opacity-60"
          data-testid="order-place-btn"
        >{busy ? 'Placing order…' : `Place order  ·  ₹${Math.round(card.total_amount)}`}</button>
      </div>
    </div>
  );
}

/**
 * Single WhatsApp-style chat bubble.
 *  side: "in"  → left (seller messages, white)
 *  side: "out" → right (buyer messages, WA green) — not used in the buyer view
 *  media: bubble contains an image; tighter padding
 */
function ChatBubble({ side = 'in', children, time, media = false, testid, outgoing = false }) {
  const isIn = side === 'in';
  const bg = isIn ? 'bg-white' : 'bg-[#d9fdd3]';
  return (
    <div className={`flex ${isIn ? 'justify-start' : 'justify-end'}`}>
      <div
        className={`relative max-w-[86%] sm:max-w-[70%] shadow-sm ${bg} ${
          media ? 'p-1 rounded-lg' : 'px-3 py-2 rounded-lg'
        } text-[14px] text-neutral-900 leading-relaxed`}
        data-testid={testid}
      >
        {children}
        <div className={`flex items-center gap-1 ${
          media
            ? 'absolute right-2 bottom-1 bg-black/40 rounded-full px-1.5 py-0.5 text-white text-[10px]'
            : 'justify-end text-[10px] text-neutral-500 mt-1'
        }`}>
          <span>{time}</span>
          {outgoing && !media && <CheckCheck className="w-3 h-3 text-[#4fc3f7]" />}
        </div>
      </div>
    </div>
  );
}

/**
 * Full-screen in-app camera capture (uses `navigator.mediaDevices.getUserMedia`).
 * Live preview → tap-to-capture photo OR press-and-hold / tap-to-record video →
 * review the captured media → Retake or Send. On desktops without a webcam,
 * or when permission is denied, calls onFallback() so the caller can trigger
 * the native OS file picker as a graceful degradation.
 *
 * Emits `onCapture(file, 'image' | 'video')` when the buyer taps Send.
 */
function CameraCapture({ onClose, onCapture, onFallback }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [mode, setMode] = useState('photo');         // 'photo' | 'video'
  const [facing, setFacing] = useState('environment');
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [preview, setPreview] = useState(null);      // { url, blob, kind }

  const startStream = React.useCallback(async (facingMode) => {
    setError('');
    setReady(false);
    // Stop any existing stream before requesting a new one (needed for flip)
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError('unsupported');
      return;
    }
    try {
      // Request BOTH video + audio so we can transition from photo → video
      // without a second permission prompt. If audio isn't available we
      // silently retry with video only.
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: facingMode }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: true,
        });
      } catch (_e) {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: facingMode }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setReady(true);
    } catch (err) {
      if (err && (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError')) {
        setError('denied');
      } else if (err && (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError')) {
        setError('no-camera');
      } else {
        setError('other');
      }
    }
  }, []);

  useEffect(() => {
    startStream(facing);
    return () => {
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        try { recorderRef.current.stop(); } catch (_e) { /* ignore */ }
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  // Recording elapsed timer
  useEffect(() => {
    if (!recording) { setElapsed(0); return; }
    const t0 = Date.now();
    const it = setInterval(() => setElapsed(Math.floor((Date.now() - t0) / 1000)), 200);
    return () => clearInterval(it);
  }, [recording]);

  const flipCamera = () => {
    const next = facing === 'environment' ? 'user' : 'environment';
    setFacing(next);
    startStream(next);
  };

  const capturePhoto = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    const ctx = canvas.getContext('2d');
    // Front cameras deliver a mirrored preview — un-mirror the saved photo
    if (facing === 'user') { ctx.translate(canvas.width, 0); ctx.scale(-1, 1); }
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      setPreview({ url, blob, kind: 'image' });
    }, 'image/jpeg', 0.92);
  };

  const startVideoRecording = () => {
    if (!streamRef.current) return;
    try {
      const mimeCandidates = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'];
      const mime = mimeCandidates.find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m)) || '';
      const rec = mime ? new MediaRecorder(streamRef.current, { mimeType: mime }) : new MediaRecorder(streamRef.current);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        if (chunksRef.current.length === 0) return;
        const type = rec.mimeType || 'video/webm';
        const blob = new Blob(chunksRef.current, { type });
        const url = URL.createObjectURL(blob);
        setPreview({ url, blob, kind: 'video' });
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch (err) {
      setError('recorder');
    }
  };
  const stopVideoRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop(); } catch (_e) { /* ignore */ }
    }
    setRecording(false);
  };

  const retake = () => {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const send = () => {
    if (!preview) return;
    const ext = preview.kind === 'image' ? 'jpg' : (preview.blob.type.includes('mp4') ? 'mp4' : 'webm');
    const filename = preview.kind === 'image'
      ? `camera-${Date.now()}.${ext}`
      : `camera-${Date.now()}.${ext}`;
    const file = new File([preview.blob], filename, { type: preview.blob.type || (preview.kind === 'image' ? 'image/jpeg' : 'video/webm') });
    onCapture(file, preview.kind);
  };

  const mm = String(Math.floor(elapsed / 60)).padStart(2, '0');
  const ss = String(elapsed % 60).padStart(2, '0');
  const errorLabel = ({
    denied: 'Camera permission denied. Enable camera access for this site in your browser settings and try again.',
    'no-camera': 'No camera was found on this device.',
    unsupported: 'Camera capture is not supported in this browser.',
    recorder: 'Recording failed to start on this device.',
    other: 'Could not open the camera. Please try again.',
  })[error];

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="camera-capture">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 text-white bg-gradient-to-b from-black/70 to-transparent">
        <button
          onClick={onClose}
          className="p-2 -ml-2 rounded-full hover:bg-white/10"
          aria-label="Close camera"
          data-testid="camera-close"
        ><X className="w-6 h-6" /></button>
        <div className="text-[13px] tabular-nums" data-testid="camera-title">
          {preview ? 'Preview' : (mode === 'photo' ? 'Photo' : (recording ? `Recording · ${mm}:${ss}` : 'Video'))}
        </div>
        <button
          onClick={flipCamera}
          disabled={!ready || recording || !!preview}
          className="p-2 -mr-2 rounded-full hover:bg-white/10 disabled:opacity-40"
          aria-label="Flip camera"
          data-testid="camera-flip"
        ><RotateCcw className="w-5 h-5" /></button>
      </div>

      {/* Preview surface */}
      <div className="flex-1 relative flex items-center justify-center overflow-hidden">
        {!preview && (
          <video
            ref={videoRef}
            playsInline
            muted
            autoPlay
            className="max-h-full max-w-full object-contain"
            style={{ transform: facing === 'user' ? 'scaleX(-1)' : 'none' }}
            data-testid="camera-video"
          />
        )}
        {preview && preview.kind === 'image' && (
          <img src={preview.url} alt="Captured" className="max-h-full max-w-full object-contain" data-testid="camera-preview-image" />
        )}
        {preview && preview.kind === 'video' && (
          <video src={preview.url} controls autoPlay playsInline className="max-h-full max-w-full object-contain" data-testid="camera-preview-video" />
        )}
        {error && (
          <div className="absolute inset-x-6 top-1/2 -translate-y-1/2 bg-white text-neutral-900 rounded-2xl p-4 shadow-2xl text-center" data-testid="camera-error">
            <div className="font-semibold text-[15px]">Camera unavailable</div>
            <div className="text-[13px] text-neutral-600 mt-1">{errorLabel}</div>
            <div className="mt-3 flex gap-2">
              <button
                onClick={onFallback}
                className="flex-1 bg-[#25D366] text-white text-[13px] font-semibold py-2.5 rounded-lg"
                data-testid="camera-fallback"
              >Pick from device</button>
              <button
                onClick={onClose}
                className="flex-1 bg-neutral-100 text-neutral-800 text-[13px] font-semibold py-2.5 rounded-lg"
              >Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* Bottom controls */}
      {!error && (
        <div className="pb-6 pt-3 bg-gradient-to-t from-black/80 to-transparent text-white">
          {!preview ? (
            <>
              {/* Mode switcher */}
              <div className="flex items-center justify-center gap-2 mb-4">
                <button
                  onClick={() => !recording && setMode('photo')}
                  className={`px-4 py-1.5 rounded-full text-[12px] font-semibold uppercase tracking-wide ${mode === 'photo' ? 'bg-white text-neutral-900' : 'text-white/80'}`}
                  data-testid="camera-mode-photo"
                >Photo</button>
                <button
                  onClick={() => !recording && setMode('video')}
                  className={`px-4 py-1.5 rounded-full text-[12px] font-semibold uppercase tracking-wide ${mode === 'video' ? 'bg-white text-neutral-900' : 'text-white/80'}`}
                  data-testid="camera-mode-video"
                >Video</button>
              </div>
              {/* Shutter */}
              <div className="flex items-center justify-center">
                {mode === 'photo' ? (
                  <button
                    onClick={capturePhoto}
                    disabled={!ready}
                    className="w-20 h-20 rounded-full bg-white flex items-center justify-center border-4 border-white/40 shadow-2xl active:scale-95 transition disabled:opacity-40"
                    aria-label="Take photo"
                    data-testid="camera-shutter-photo"
                  ><Circle className="w-16 h-16 text-white fill-white" /></button>
                ) : recording ? (
                  <button
                    onClick={stopVideoRecording}
                    className="w-20 h-20 rounded-full bg-red-600 flex items-center justify-center border-4 border-white/40 shadow-2xl active:scale-95 transition"
                    aria-label="Stop recording"
                    data-testid="camera-shutter-stop"
                  ><Square className="w-8 h-8 text-white fill-white" /></button>
                ) : (
                  <button
                    onClick={startVideoRecording}
                    disabled={!ready}
                    className="w-20 h-20 rounded-full bg-red-500 flex items-center justify-center border-4 border-white/40 shadow-2xl active:scale-95 transition disabled:opacity-40"
                    aria-label="Start recording"
                    data-testid="camera-shutter-video"
                  ><Circle className="w-14 h-14 text-white fill-white" /></button>
                )}
              </div>
              {!ready && !error && (
                <div className="mt-3 flex items-center justify-center gap-2 text-[12px] text-white/80" data-testid="camera-loading">
                  <Loader2 className="w-4 h-4 animate-spin" /> Starting camera…
                </div>
              )}
            </>
          ) : (
            /* Preview actions */
            <div className="flex items-center justify-between px-8">
              <button
                onClick={retake}
                className="flex flex-col items-center gap-1 text-white"
                data-testid="camera-retake"
              >
                <span className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center">
                  <RefreshCw className="w-6 h-6" />
                </span>
                <span className="text-[12px]">Retake</span>
              </button>
              <button
                onClick={send}
                className="flex flex-col items-center gap-1 text-white"
                data-testid="camera-send"
              >
                <span className="w-14 h-14 rounded-full bg-[#25D366] flex items-center justify-center shadow-xl">
                  <Send className="w-6 h-6 text-white" />
                </span>
                <span className="text-[12px]">Send</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}



// ═══════════════════════════════════════════════════════════════════════════
// Commerce helpers — payment instructions bubble, service worker, push utils
// ═══════════════════════════════════════════════════════════════════════════

// Feb 2026 — Full-screen product image viewer. Tap product → open, tap X /
// backdrop / Esc → close. Supports Save (fetch → blob → anchor download,
// works cross-platform: Android Chrome/Firefox, iOS Safari opens the blob
// so long-press-save-image works, desktop downloads immediately) and Share
// (`navigator.share({files})` where supported, falls back to opening the
// image URL in a new tab so the buyer can long-press or use the browser's
// own share). Zero backend change — the image is already publicly served
// at the same URL the <img src=> uses. Same origin fetch, no CORS surface.
function ImageViewer({ src, alt, onClose }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const fileName = () => {
    const cleaned = (alt || 'product').replace(/[^A-Za-z0-9._-]+/g, '_');
    const ext = (() => {
      const m = src.match(/\.(jpe?g|png|webp|gif|avif)(\?|$)/i);
      return m ? m[1].toLowerCase() : 'jpg';
    })();
    return `${cleaned || 'product'}.${ext === 'jpeg' ? 'jpg' : ext}`;
  };

  const onSave = async () => {
    if (busy) return;
    setBusy(true); setMsg('');
    try {
      const resp = await fetch(src, { credentials: 'omit' });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName();
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      setMsg('Saved to your device');
    } catch (_e) {
      // iOS-safe fallback — open in a new tab so buyer can long-press-save
      try { window.open(src, '_blank', 'noopener'); setMsg('Opened image — long-press to save'); }
      catch (_e2) { setMsg('Could not save image'); }
    } finally { setBusy(false); }
  };

  const onShare = async () => {
    if (busy) return;
    setBusy(true); setMsg('');
    try {
      let shared = false;
      if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
        try {
          const resp = await fetch(src, { credentials: 'omit' });
          const blob = await resp.blob();
          const file = new File([blob], fileName(), { type: blob.type || 'image/jpeg' });
          const data = { files: [file], title: alt || 'Product', text: alt || '' };
          const canFiles = typeof navigator.canShare === 'function' ? navigator.canShare(data) : true;
          if (canFiles) {
            await navigator.share(data);
            shared = true;
          } else {
            await navigator.share({ title: alt || 'Product', text: alt || '', url: src });
            shared = true;
          }
        } catch (err) {
          if (err && err.name === 'AbortError') { shared = true; /* user cancelled — treat as OK */ }
        }
      }
      if (!shared) {
        window.open(src, '_blank', 'noopener');
        setMsg('Opened image — use your browser to share');
      }
    } finally { setBusy(false); }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${alt || 'Product'} image viewer`}
      className="fixed inset-0 z-[70] bg-black/95 flex flex-col"
      data-testid="product-image-viewer"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <div className="text-[13.5px] font-medium truncate max-w-[70%]">{alt || 'Product'}</div>
        <button
          type="button"
          onClick={onClose}
          className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 active:scale-95 flex items-center justify-center"
          aria-label="Close viewer"
          data-testid="product-image-close"
        ><X className="w-5 h-5" /></button>
      </div>
      {/* Image */}
      <div className="flex-1 flex items-center justify-center px-3 py-1" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <img
          src={src}
          alt={alt || 'Product'}
          className="max-h-full max-w-full object-contain select-none"
          draggable={false}
          data-testid="product-image-full"
        />
      </div>
      {/* Actions */}
      <div className="px-4 pb-6 pt-3 flex items-center justify-center gap-4 text-white">
        <button
          type="button"
          onClick={onSave}
          disabled={busy}
          className="flex flex-col items-center gap-1 disabled:opacity-60"
          data-testid="product-image-save"
        >
          <span className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center">
            <Download className="w-5 h-5" />
          </span>
          <span className="text-[11.5px]">Save</span>
        </button>
        <button
          type="button"
          onClick={onShare}
          disabled={busy}
          className="flex flex-col items-center gap-1 disabled:opacity-60"
          data-testid="product-image-share"
        >
          <span className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center">
            <Share2 className="w-5 h-5" />
          </span>
          <span className="text-[11.5px]">Share</span>
        </button>
      </div>
      {msg && (
        <div className="pb-3 text-center text-[12px] text-white/80" data-testid="product-image-msg">{msg}</div>
      )}
    </div>
  );
}

/**
 * WhatsApp-style card that shows the seller's payment instructions
 * (UPI ID / QR / Bank) right after the order is placed. Below the details
 * are two actions: upload a screenshot, or send "Paid" as a message. Both
 * hit `/chat/payment-proof` and inject the resulting bubbles into the chat.
 */
function PaymentInstructionsBubble({ idx, time, data, backendUrl, collectionId, sessionId, superseded }) {
  const p = data.instructions || {};
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const qr = p.qr_code_url && (p.qr_code_url.startsWith('http') ? p.qr_code_url : `${backendUrl}${p.qr_code_url}`);
  const submit = async (file, textOverride) => {
    if (busy || superseded) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('session_id', sessionId);
      fd.append('order_id', data.order_id || '');
      if (textOverride) fd.append('text', textOverride);
      if (file) fd.append('file', file);
      await axios.post(`${backendUrl}/api/collections/${collectionId}/chat/payment-proof`, fd);
      const { data: hist } = await axios.get(`${backendUrl}/api/collections/${collectionId}/chat/${sessionId}`);
      // Broadcast refreshed history so the parent replaces its chat state.
      window.dispatchEvent(new CustomEvent('fabtexs-chat-refresh', { detail: hist.messages || [] }));
          } catch (_e) { 
      console.error('Payment proof submission failed:', _e.response?.status, _e.response?.data || _e.message);
      alert(`Could not submit payment proof: ${_e.response?.data?.detail || _e.message || 'Please try again.'}`);
    } finally { setBusy(false); }
  };
  return (
    <div className="flex justify-start" data-testid={`chat-msg-payment-instructions-${idx}`}>
      <div className="max-w-[92%] sm:max-w-[80%] bg-white rounded-xl shadow-sm p-3 border-l-4 border-[#25D366]">
        <div className="text-[13px] font-semibold text-neutral-900">💳 Payment Details</div>
        <div className="mt-1 text-[13px] text-neutral-800">Please pay <span className="font-bold text-[#25D366]">₹{Math.round(data.amount || 0)}</span> using any of the details below.</div>
        <div className="mt-2 space-y-1 text-[12.5px] text-neutral-700">
          {p.upi_id && <div>UPI: <span className="font-mono text-neutral-900">{p.upi_id}</span></div>}
          {qr && (
            <div className="my-2">
              <div className="inline-block bg-white p-3 border" data-testid="payment-qr-image-wrap">
                <img src={qr} alt="Scan to pay" className="w-56 h-56 object-contain" data-testid="payment-qr-image" />
              </div>
              <a
                href={qr}
                target="_blank"
                rel="noopener noreferrer"
                className="block mt-1 text-[12px] font-semibold text-[#25D366] underline"
                data-testid="payment-qr-view-full"
              >
                View Full QR
              </a>
            </div>
          )}
          {p.bank_account && (
            <div className="mt-1">
              <div>Bank: <span className="font-medium text-neutral-900">{p.bank_name || '—'}</span></div>
              <div>A/C: <span className="font-mono text-neutral-900">{p.bank_account}</span></div>
              {p.bank_ifsc && <div>IFSC: <span className="font-mono text-neutral-900">{p.bank_ifsc}</span></div>}
            </div>
          )}
          {!p.upi_id && !p.qr_code_url && !p.bank_account && (
            <div className="text-[12px] text-neutral-500">The seller will share payment details shortly. Once you have paid, tap &quot;I&apos;ve paid&quot; or share a screenshot below.</div>
          )}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            onClick={() => fileRef.current?.click()}
            disabled={busy || superseded}
            className="bg-white border border-[#25D366] text-[#25D366] text-[12.5px] font-semibold py-2 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`pay-upload-btn-${idx}`}
          >📷 Upload screenshot</button>
          <button
            onClick={() => submit(null, 'Payment done')}
            disabled={busy || superseded}
            className="bg-[#25D366] text-white text-[12.5px] font-semibold py-2 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`pay-done-btn-${idx}`}
          >{superseded ? 'Already processed' : (busy ? 'Sending…' : "I've paid")}</button>
        </div>
        <input ref={fileRef} type="file" accept="image/*" className="hidden"
               data-testid={`pay-file-input-${idx}`}
               onChange={(e) => { const f = e.target.files?.[0]; if (f) submit(f, null); e.target.value = ''; }} />
        <div className="text-right text-[10px] text-neutral-500 mt-2">{time}</div>
      </div>
    </div>
  );
}

/**
 * "Rate your order" prompt bubble — auto-inserted into the persistent chat
 * right after the seller dispatches. Buyer taps 1-5 stars → posts to
 * `/chat/rate` → seller + platform rating aggregates update server-side.
 * One review per order (backend enforces 409 on retry).
 */
function ParcelReceivedBubble({ idx, time, data, done, backendUrl, collectionId, sessionId }) {
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const isDone = done || confirmed;
  const confirm = async () => {
    if (busy || isDone) return;
    setBusy(true);
    try {
      await axios.post(`${backendUrl}/api/collections/${collectionId}/orders/${data.order_id}/parcel-received`, {
        session_id: sessionId,
        order_id: data.order_id,
      });
      setConfirmed(true);
      // Refetch so the star-rating prompt appears immediately.
      const { data: hist } = await axios.get(`${backendUrl}/api/collections/${collectionId}/chat/${sessionId}`);
      window.dispatchEvent(new CustomEvent('fabtexs-chat-refresh', { detail: hist.messages || [] }));
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not confirm receipt. Please try again.');
    } finally { setBusy(false); }
  };
  return (
    <div className="flex justify-start" data-testid={`chat-msg-parcel-received-${idx}`}>
      <div className="max-w-[92%] sm:max-w-[80%] bg-white rounded-xl shadow-sm p-3 border-l-4 border-[#075E54]">
        <div className="text-[13.5px] text-neutral-800 whitespace-pre-line">{data.text}</div>
        <button
          onClick={confirm}
          disabled={busy || isDone}
          className="mt-3 w-full bg-[#075E54] text-white text-[13px] font-semibold py-2 rounded-lg disabled:opacity-50"
          data-testid={`parcel-received-btn-${idx}`}
        >{isDone ? 'Receipt Confirmed ✓' : (busy ? 'Confirming…' : 'Parcel Received')}</button>
        <div className="text-right text-[10px] text-neutral-500 mt-2">{time}</div>
      </div>
    </div>
  );
}

function RateRequestBubble({ idx, time, data, rated, ratedValue, backendUrl, collectionId, sessionId }) {
  const [hover, setHover] = useState(0);
  const [chosen, setChosen] = useState(0);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const isRated = rated || submitted;
  // Once rated, always reflect the rating actually on record (from the
  // server-persisted rating_thanks message) rather than the ephemeral local
  // `chosen` selection — `chosen` resets to 0 on remount/reload, which was
  // making the stars appear to reset even though the order stayed rated.
  const displayRating = isRated ? (ratedValue || chosen) : chosen;
  const submit = async () => {
    if (busy || isRated || !chosen) return;
    setBusy(true);
    try {
      await axios.post(`${backendUrl}/api/collections/${collectionId}/chat/rate`, {
        session_id: sessionId,
        order_id: data.order_id,
        rating: chosen,
      });
      setSubmitted(true);
      // Refetch chat so the rating_thanks bubble appears
      const { data: hist } = await axios.get(`${backendUrl}/api/collections/${collectionId}/chat/${sessionId}`);
      window.dispatchEvent(new CustomEvent('fabtexs-chat-refresh', { detail: hist.messages || [] }));
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not submit rating. Please try again.');
    } finally { setBusy(false); }
  };
  return (
    <div className="flex justify-start" data-testid={`chat-msg-rate-request-${idx}`}>
      <div className="max-w-[92%] sm:max-w-[80%] bg-white rounded-xl shadow-sm p-3 border-l-4 border-amber-400">
        <div className="text-[13.5px] text-neutral-800 whitespace-pre-line">{data.text}</div>
        <div className="mt-3 flex items-center gap-1" onMouseLeave={() => setHover(0)}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              onMouseEnter={() => !isRated && setHover(n)}
              onClick={() => !isRated && setChosen(n)}
              disabled={isRated}
              className="text-[28px] leading-none focus:outline-none disabled:cursor-default"
              data-testid={`rate-star-${idx}-${n}`}
              aria-label={`Rate ${n} stars`}
            >
              <span className={(displayRating ? n <= displayRating : n <= hover) ? 'text-amber-500' : 'text-neutral-300'}>★</span>
            </button>
          ))}
        </div>
        {chosen > 0 && !isRated && (
          <button
            onClick={submit}
            disabled={busy}
            className="mt-3 w-full bg-amber-500 text-white text-[13px] font-semibold py-2 rounded-lg disabled:opacity-60"
            data-testid={`rate-submit-${idx}`}
          >{busy ? 'Submitting…' : 'Submit Rating'}</button>
        )}
        <div className="text-right text-[10px] text-neutral-500 mt-2">{time}</div>
      </div>
    </div>
  );
}



